import { Field, FieldArray, FieldProps } from "formik";
import React from "react";
import { Button, Col, Label, Row } from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";

import QuantityField from "./QuantityField";
import {
  MAX_E_VALUE_DECIMALS,
  MAX_E_VALUE_DIGITS,
  MAX_LCV_DECIMALS,
  MAX_LCV_DIGITS,
  MAX_PERCENT_DECIMALS,
  MAX_PERCENT_DIGITS,
  MAX_QUANTITY_DECIMALS,
  MAX_QUANTITY_DIGITS,
} from "./common";
import {
  FuelEURow,
  FuelFactor,
  BIO_BLEND_ID,
  emptyFuelRow,
  formatNumber,
  isBioBlend,
  needsOverride,
  usesEValue,
} from "./fueleu";

/**
 * The fuel-mix half of a FuelEU form: the repeating fuel rows, the bio-blend
 * panel, the override boxes, and the validation that decides which of those
 * boxes are required.
 *
 * Shared by the two calculators, and by the daily one twice over. All three
 * mixes ask for the same thing and differ only in what a quantity means — tonnes
 * bunkered over the period, tonnes burnt per day, or tonnes already burnt — which
 * is the `unitLabel` prop and nothing else. Everything that decides whether a
 * fuel needs an E value, whether a blend adds to 100%, and how a fuel row looks
 * is the same question in all of them, and was worth one answer: this file is a
 * move out of FuelEUCalculator.tsx, not a second implementation of it.
 *
 * `arrayName` is the Formik field the rows live under. It defaults to "fuels",
 * and the daily form's already-consumed table passes "consumed_fuels" — which is
 * also what keeps the duplicate-fuel check per table, since burning HFO both
 * before and during the plan is the ordinary case rather than a mistake.
 */

/**
 * True when `factorId` is already chosen in a different row.
 *
 * The same (fuel, engine) pair twice is not two fuels — it is one quantity
 * entered in two boxes, and the totals would be right while the breakdown showed
 * a mix that does not exist. Mirrors `isFuelSelectedElsewhere` in common.ts,
 * which does the same job for the CII form's three fixed slots.
 */
export const isFuelChosenElsewhere = (
  rows: FuelEURow[],
  currentIndex: number,
  factorId: number
): boolean =>
  (rows ?? []).some(
    (row, index) => index !== currentIndex && row.fuel_factor === factorId
  );

/**
 * The validation message for one field of one fuel row.
 *
 * Yup reports these at `errors.fuels[i].<field>`, and Formik only surfaces them
 * once the field is touched — but these are "you have not filled this in yet"
 * errors on fields that appear mid-form, so they are shown on submit regardless
 * of touch. `submitCount` is what distinguishes "not filled in yet" from "not
 * filled in and you tried to calculate".
 */
export const RowError = ({
  form,
  index,
  field,
  arrayName = "fuels",
}: {
  form: any;
  index: number;
  field: string;
  arrayName?: string;
}) => {
  if (!form.submitCount) return null;
  const message = form.errors?.[arrayName]?.[index]?.[field];
  if (!message) return null;
  return <Label className="error d-block">{message}</Label>;
};

/**
 * The MJ/kg equivalent of an LCV, echoed live beneath the box.
 *
 * The box takes MJ/g, which is what FuelEU's Annex II header specifies and what
 * `tbl_fuel_multiplying_factor` stores. The label beside it points at RED II
 * Annex III, which publishes the same quantity by weight — 37 MJ/kg for FAME,
 * 44 for HVO. Read the value there and type it as published and you are out by
 * a factor of a thousand, with no error and a fuel a thousand times more
 * energy-dense than any fuel.
 *
 * A static "e.g. 0.037" placeholder does not catch that; it vanishes the moment
 * anything is typed. This does: enter 37 and it reads "= 37,000 MJ/kg", which
 * is not a number anyone mistakes for a fuel.
 */
export const LcvEcho = ({ value }: { value: any }) => {
  const lcv = Number(String(value ?? "").trim());
  if (!Number.isFinite(lcv) || lcv <= 0) return null;

  // Marine fuels sit between roughly 18 and 120 MJ/kg. Outside that, say so.
  const perKg = lcv * 1000;
  const implausible = perKg < 5 || perKg > 200;

  return (
    <span className={`fueleu-lcv-echo${implausible ? " fueleu-lcv-echo--off" : ""}`}>
      = {formatNumber(perKg, perKg >= 100 ? 0 : 1)} MJ/kg
      {implausible && " — check the unit; RED II Annex III publishes MJ/kg"}
    </span>
  );
};

/**
 * The two halves of a bio blend, shown when "Bio blend" is the selected fuel.
 *
 * A blend has no emission factors of its own - it is the two fuels it is made
 * of, and Annex I has no concept of a blend at all, only a list of fuels. So
 * this collects the split and `buildPayload` posts two real fuels. Entering the
 * components by hand as two rows gives exactly the same answer; this exists
 * because operators bunker and think in blends.
 *
 * The bio half is fixed to bio-diesel and shown disabled rather than omitted, so
 * the row reads as two components rather than one fuel with settings.
 *
 * Percentages are **by mass**, which is how a bunkered blend is specified. The
 * complement auto-fills, because "70 / 30" is one decision and making the user
 * state it twice only creates a way to get it wrong - but both stay editable,
 * and the pair is validated to 100 either way.
 */
export const BioBlendPanel = ({
  index,
  row,
  fossilOptions,
  form,
  unitLabel,
  arrayName = "fuels",
}: {
  index: number;
  row: FuelEURow;
  fossilOptions: FuelFactor[];
  form: any;
  unitLabel: string;
  arrayName?: string;
}) => {
  const bio = Number(row.blend_bio_percent);
  const fossil = Number(row.blend_fossil_percent);
  const sum = (Number.isFinite(bio) ? bio : 0) + (Number.isFinite(fossil) ? fossil : 0);
  const total = Number(row.quantity) || 0;

  const setSplit = (bioPercent: number) => {
    form.setFieldValue(`${arrayName}.${index}.blend_bio_percent`, bioPercent);
    form.setFieldValue(`${arrayName}.${index}.blend_fossil_percent`, 100 - bioPercent);
  };

  const fossilFactor = fossilOptions.find(
    (f) => f.id === row.blend_fossil_factor
  );

  return (
    <div className="fueleu-blend">
      <p className="fueleu-hint mb-2">
        A blend is posted as its two components. Percentages are by mass of the
        quantity above.
      </p>

      <Row className="align-items-end">
        <Col xs={12} md={5}>
          <Label className="mb-0">Biofuel component</Label>
          <Select
            options={[]}
            value={{ label: "BIO DIESEL" }}
            getOptionLabel={(o: any) => o.label}
            isDisabled
            className="mb-2"
          />
        </Col>
        <Col xs={7} md={3}>
          <Label className="mb-0">Share</Label>
          <QuantityField
            name={`${arrayName}.${index}.blend_bio_percent`}
            unit="%"
            maxWholeDigits={MAX_PERCENT_DIGITS}
            maxDecimals={MAX_PERCENT_DECIMALS}
            max={100}
            onValue={(text) => {
              const value = Number(text);
              if (text !== "" && Number.isFinite(value)) setSplit(value);
              else form.setFieldValue(
                `${arrayName}.${index}.blend_bio_percent`, text);
            }}
          />
        </Col>
        <Col xs={5} md={4}>
          <span className="fueleu-blend__mass">
            {formatNumber((total * (bio || 0)) / 100, 2)} {unitLabel}
          </span>
        </Col>
      </Row>

      <Row className="align-items-end">
        <Col xs={12} md={5}>
          <Label className="mb-0 asteric">Fossil component</Label>
          <Field name={`${arrayName}.${index}.blend_fossil_factor`}>
            {({ field }: FieldProps) => (
              <Select
                options={fossilOptions}
                value={fossilFactor ?? null}
                getOptionLabel={(o: any) => o.label}
                getOptionValue={(o: any) => String(o.id)}
                onChange={(option: any) =>
                  form.setFieldValue(field.name, option?.id ?? null)
                }
                isClearable
                menuPortalTarget={document.body}
                className="mb-2"
                placeholder="HFO or Diesel Oil"
              />
            )}
          </Field>
        </Col>
        <Col xs={7} md={3}>
          <Label className="mb-0">Share</Label>
          <QuantityField
            name={`${arrayName}.${index}.blend_fossil_percent`}
            unit="%"
            maxWholeDigits={MAX_PERCENT_DIGITS}
            maxDecimals={MAX_PERCENT_DECIMALS}
            max={100}
            onValue={(text) => {
              const value = Number(text);
              if (text !== "" && Number.isFinite(value)) setSplit(100 - value);
              else form.setFieldValue(
                `${arrayName}.${index}.blend_fossil_percent`, text);
            }}
          />
        </Col>
        <Col xs={5} md={4}>
          <span className="fueleu-blend__mass">
            {formatNumber((total * (fossil || 0)) / 100, 2)} {unitLabel}
          </span>
        </Col>
      </Row>

      {Math.abs(sum - 100) > 0.001 && (
        <Label className="error d-block">
          The two shares must add up to 100% — they currently total{" "}
          {formatNumber(sum, 1)}%.
        </Label>
      )}

      {/* Annex II publishes no LCV and no well-to-tank term for bio-diesel, so
          the bio half cannot be priced without these. Asked for here rather than
          in the generic override panel because only one of the two components
          needs them. */}
      <Row className="fueleu-override">
        <Col xs={12}>
          <p className="fueleu-hint mb-2">
            Annex II derives bio-diesel&rsquo;s well-to-tank term from its
            pathway: WtT = E &minus; C_fCO&#8322; &divide; LCV. Both values come
            from the supplier&rsquo;s proof of sustainability.
          </p>
        </Col>
        <Col xs={12} md={5}>
          <Label className="mb-0 asteric">
            Bio-diesel E value (RED II Annex V)
          </Label>
          <QuantityField
            name={`${arrayName}.${index}.e_value`}
            placeholder="e.g. 35"
            unit="gCO₂eq/MJ"
            maxWholeDigits={MAX_E_VALUE_DIGITS}
            maxDecimals={MAX_E_VALUE_DECIMALS}
          />
          <RowError
            form={form}
            index={index}
            field="e_value"
            arrayName={arrayName}
          />
        </Col>
        <Col xs={12} md={5}>
          <Label className="mb-0 asteric">
            Bio-diesel LCV (RED II Annex III)
          </Label>
          <QuantityField
            name={`${arrayName}.${index}.lcv_override`}
            placeholder="e.g. 0.037"
            unit="MJ/g"
            maxWholeDigits={MAX_LCV_DIGITS}
            maxDecimals={MAX_LCV_DECIMALS}
          />
          <LcvEcho value={row.lcv_override} />
          <RowError
            form={form}
            index={index}
            field="lcv_override"
            arrayName={arrayName}
          />
        </Col>
      </Row>
    </div>
  );
};

/* --------------------------------------------------------- requirements ---
 * Which of a row's optional boxes are actually required, given the fuel picked
 * in it. Built as a factory over `factorsById` rather than read from the row,
 * because the row stores only the factor id and the answer lives in the factor.
 *
 * Shared with the Yup schema below, so the box that turns red and the box the
 * schema demands are decided by one function rather than by two that agree
 * today.
 */
export const fuelRowRequirements = (factorsById: Map<number, FuelFactor>) => {
  /** A row the user has actually committed to: a fuel and a quantity. */
  const rowIsActive = (row: FuelEURow) =>
    !!row?.fuel_factor && Number(row.quantity) > 0;

  /** Rows whose fuel Annex II describes by its pathway E value. */
  const rowNeedsE = (row: FuelEURow) =>
    rowIsActive(row) &&
    (isBioBlend(row) || usesEValue(factorsById.get(row.fuel_factor as number)));

  /** Rows that need a certified well-to-wake intensity instead - the RFNBOs. */
  const rowNeedsIntensity = (row: FuelEURow) =>
    rowIsActive(row) &&
    !isBioBlend(row) &&
    needsOverride(factorsById.get(row.fuel_factor as number)) &&
    !usesEValue(factorsById.get(row.fuel_factor as number));

  /**
   * LCV is required only where the factor table has none. The RFNBO rows carry
   * theirs, so demanding it again would be asking for a number the calculator
   * already holds. A blend's LCV belongs to its bio-diesel half, which has none.
   */
  const rowNeedsLcv = (row: FuelEURow) => {
    if (!rowIsActive(row)) return false;
    if (isBioBlend(row)) return true;
    const factor = factorsById.get(row.fuel_factor as number);
    return needsOverride(factor) && !factor?.lcv;
  };

  return { rowIsActive, rowNeedsE, rowNeedsIntensity, rowNeedsLcv };
};

const isFilled = (value: any) => String(value ?? "").trim() !== "";

/**
 * The `fuels` half of either calculator's validation schema.
 *
 * A fuel with no published factors cannot be priced without them, and the API's
 * response to that is to leave it out of the calculation entirely — correct, but
 * it means the result silently describes less fuel than the ship burns. Better
 * to refuse the submission than to record a number computed over a mix the
 * operator did not enter. `MixIntensity` still warns during the live preview,
 * which is what guides someone toward filling these in.
 *
 * Written as per-field tests rather than one array-level check so the error
 * lands under the box that needs filling. Yup's `this.parent` is the row, which
 * is what makes the row's requirements knowable from inside the schema.
 */
export const fuelMixSchema = (
  factorsById: Map<number, FuelFactor>,
  /**
   * When the table has to contain something. Defaults to always — the planned
   * mix is the input, and a calculation over no fuel is not a calculation. The
   * daily form's already-consumed table passes a predicate instead, because it
   * is only an input once the operator says part of the period has gone.
   *
   * Takes the whole form values, since that is where the answer lives: a
   * per-array rule cannot see `elapsed_days`.
   */
  isRequired: (values: any) => boolean = () => true
) => {
  const { rowNeedsE, rowNeedsIntensity, rowNeedsLcv } =
    fuelRowRequirements(factorsById);

  return Yup.array()
    .of(
      Yup.object({
        fuel_factor: Yup.number().nullable(),
        quantity: Yup.number().min(0, "Quantity cannot be negative"),
        e_value: Yup.mixed().test(
          "e-required",
          "Required — Annex II derives this fuel's WtT from its E value.",
          function (value) {
            return !rowNeedsE(this.parent) || isFilled(value);
          }
        ),
        intensity_override: Yup.mixed().test(
          "intensity-required",
          "Required — Annex II publishes no default for this fuel.",
          function (value) {
            return !rowNeedsIntensity(this.parent) || isFilled(value);
          }
        ),
        lcv_override: Yup.mixed().test(
          "lcv-required",
          "Required — the factor table has no LCV for this fuel.",
          function (value) {
            return !rowNeedsLcv(this.parent) || isFilled(value);
          }
        ),
      })
    )
    // The mix is the input. Everything else on the form qualifies it.
    .test(
      "has-a-fuel",
      "Add at least one fuel with a quantity.",
      function (rows: any) {
        if (!isRequired(this.parent)) return true;
        return (rows ?? []).some(
          (row: FuelEURow) => row.fuel_factor && Number(row.quantity) > 0
        );
      }
    )
    // A blend that does not add to 100% is not a description of anything: the
    // expansion would post less (or more) fuel than the operator bunkered,
    // and the compliance balance would be computed over the wrong quantity.
    .test(
      "blend-shares-total-100",
      "Each bio blend's two shares must add up to 100%.",
      (rows: any) =>
        (rows ?? [])
          .filter((row: FuelEURow) => isBioBlend(row) && Number(row.quantity) > 0)
          .every(
            (row: FuelEURow) =>
              Math.abs(
                (Number(row.blend_bio_percent) || 0) +
                  (Number(row.blend_fossil_percent) || 0) -
                  100
              ) < 0.001
          )
    )
    // E and LCV are covered per-field above; this is the half that has no
    // field of its own to hang an error on.
    .test(
      "blend-has-a-fossil-component",
      "Each bio blend needs a fossil component.",
      (rows: any) =>
        (rows ?? [])
          .filter((row: FuelEURow) => isBioBlend(row) && Number(row.quantity) > 0)
          .every((row: FuelEURow) => !!row.blend_fossil_factor)
    );
};

/* ------------------------------------------------------------ the fields ---*/

interface FuelMixFieldsProps {
  /** Formik's render props for the whole form. */
  form: any;
  /** Every pickable option, including the synthetic Bio blend row. */
  factorsById: Map<number, FuelFactor>;
  /** Those options grouped by fuel class, as react-select wants them. */
  fuelGroups: any[];
  /** The fossil fuels a bio-diesel blend may be cut with. */
  blendFossilOptions: FuelFactor[];
  isCompact: boolean;
  /** "mt" for a period total, "mt/day" for a daily rate. */
  unitLabel: string;
  /** Column heading over the quantity box. */
  quantityLabel: string;
  /**
   * Whole digits allowed in the quantity, which depends on what one means: a
   * period total runs to six figures, a daily rate to three.
   */
  quantityWholeDigits?: number;
  /** The Formik array these rows live under. Defaults to "fuels". */
  arrayName?: string;
}

/**
 * The repeating fuel rows, add/remove buttons included.
 *
 * Renders into the caller's Formik context under the field name `fuels`, so the
 * two forms differ only in what they wrap it with.
 */
export const FuelMixFields = ({
  form,
  factorsById,
  fuelGroups,
  blendFossilOptions,
  isCompact,
  unitLabel,
  quantityLabel,
  quantityWholeDigits = MAX_QUANTITY_DIGITS,
  arrayName = "fuels",
}: FuelMixFieldsProps) => (
  <FieldArray name={arrayName}>
    {({ push, remove }) => (
      <>
        {!isCompact && (
          <Row className="mb-1">
            <Col md={7}>
              <Label className="asteric">Fuel / engine</Label>
            </Col>
            {/* Three columns, not four: the twelfth is not enough room for the
                remove button, which is positioned over the space left here. */}
            <Col md={3}>
              <Label className="asteric">{quantityLabel}</Label>
            </Col>
          </Row>
        )}

        {(form.values[arrayName] ?? []).map((row: FuelEURow, index: number) => {
          const factor = row.fuel_factor
            ? factorsById.get(row.fuel_factor)
            : undefined;
          const blend = isBioBlend(row);
          const override = !blend && needsOverride(factor);

          return (
            <div className="fueleu-fuel-row" key={index}>
              <Row className="align-items-start">
                <Col xs={12} md={7}>
                  <Field name={`${arrayName}.${index}.fuel_factor`}>
                    {({ field, form: inner }: FieldProps) => (
                      <Select
                        options={fuelGroups}
                        value={factor ?? null}
                        getOptionLabel={(option: any) => option.label}
                        getOptionValue={(option: any) => String(option.id)}
                        isOptionDisabled={(option: any) =>
                          isFuelChosenElsewhere(
                            (inner.values as any)[arrayName],
                            index,
                            option.id
                          )
                        }
                        onChange={(option: any) => {
                          inner.setFieldValue(field.name, option?.id ?? null);
                          // A carried-over override belongs to the fuel it was
                          // typed for. Leaving it behind would silently apply
                          // one fuel's certified intensity to another.
                          inner.setFieldValue(
                            `${arrayName}.${index}.intensity_override`,
                            ""
                          );
                          inner.setFieldValue(
                            `${arrayName}.${index}.lcv_override`,
                            ""
                          );
                          inner.setFieldValue(`${arrayName}.${index}.e_value`, "");
                          // Blend fields belong to the blend. Left behind, a
                          // later switch back to Bio blend would silently
                          // reuse the last split.
                          inner.setFieldValue(
                            `${arrayName}.${index}.blend_fossil_factor`,
                            null
                          );
                          inner.setFieldValue(
                            `${arrayName}.${index}.blend_bio_percent`,
                            option?.id === BIO_BLEND_ID ? 30 : ""
                          );
                          inner.setFieldValue(
                            `${arrayName}.${index}.blend_fossil_percent`,
                            option?.id === BIO_BLEND_ID ? 70 : ""
                          );
                        }}
                        isClearable
                        menuPortalTarget={document.body}
                        className="mb-2"
                        placeholder="Select a fuel"
                      />
                    )}
                  </Field>
                </Col>

                <Col xs={12} md={3}>
                  <QuantityField
                    id={`${arrayName}.${index}.quantity`}
                    name={`${arrayName}.${index}.quantity`}
                    placeholder={isCompact ? quantityLabel : ""}
                    unit={unitLabel}
                    maxWholeDigits={quantityWholeDigits}
                    maxDecimals={MAX_QUANTITY_DECIMALS}
                  />
                </Col>
              </Row>

              {blend && (
                <BioBlendPanel
                  index={index}
                  row={row}
                  fossilOptions={blendFossilOptions}
                  form={form}
                  unitLabel={unitLabel}
                  arrayName={arrayName}
                />
              )}

              {override && (
                <Row className="fueleu-override">
                  <Col xs={12}>
                    <p className="fueleu-hint mb-2">
                      {usesEValue(factor)
                        ? "Annex II derives a biofuel's well-to-tank term from its pathway: WtT = E − C_fCO₂ ÷ LCV. Enter the E value and the LCV from the supplier's proof of sustainability."
                        : "Annex II publishes no default for this fuel — it expects a certified pathway value for the bunker actually supplied. Enter both to include it in the calculation."}
                    </p>
                  </Col>
                  <Col xs={12} md={5}>
                    {usesEValue(factor) ? (
                      <>
                        <Label className="mb-0 asteric">
                          E value (RED II Annex V)
                        </Label>
                        <QuantityField
                          name={`${arrayName}.${index}.e_value`}
                          placeholder="e.g. 35"
                          unit="gCO₂eq/MJ"
                          maxWholeDigits={MAX_E_VALUE_DIGITS}
                          maxDecimals={MAX_E_VALUE_DECIMALS}
                        />
                        <RowError
            form={form}
            index={index}
            field="e_value"
            arrayName={arrayName}
          />
                      </>
                    ) : (
                      <>
                        <Label className="mb-0 asteric">
                          Certified GHG intensity
                        </Label>
                        <div className="input-group mb-2">
                          <Field
                            type="text"
                            inputMode="decimal"
                            name={`${arrayName}.${index}.intensity_override`}
                            className="form-control"
                            placeholder="e.g. 20"
                          />
                          <div className="input-group-text">gCO₂eq/MJ</div>
                        </div>
                        <RowError
                          form={form}
                          index={index}
                          field="intensity_override"
                          arrayName={arrayName}
                        />
                      </>
                    )}
                  </Col>
                  <Col xs={12} md={5}>
                    <Label className={`mb-0${factor?.lcv ? "" : " asteric"}`}>
                      {usesEValue(factor) ? "LCV (RED II Annex III)" : "LCV"}
                    </Label>
                    <QuantityField
                      name={`${arrayName}.${index}.lcv_override`}
                      placeholder="e.g. 0.044"
                      unit="MJ/g"
                      maxWholeDigits={MAX_LCV_DIGITS}
                      maxDecimals={MAX_LCV_DECIMALS}
                    />
                    <LcvEcho value={row.lcv_override} />
                    <RowError
                      form={form}
                      index={index}
                      field="lcv_override"
                      arrayName={arrayName}
                    />
                  </Col>
                </Row>
              )}

              {(form.values[arrayName] ?? []).length > 1 && (
                <Button
                  type="button"
                  className="btn fueleu-remove"
                  onClick={() => remove(index)}
                >
                  Remove this fuel
                </Button>
              )}
            </div>
          );
        })}

        <Button
          type="button"
          className="btn fueleu-add"
          onClick={() => push(emptyFuelRow())}
        >
          + Add another fuel
        </Button>
      </>
    )}
  </FieldArray>
);
